import { Box } from '@atgfr8/atg-ui-tailwind';
import { Button, Tab, TabsComponent } from '@components';
import { getDB } from '@database';
import {
  getAllBillingHolds,
  getAlloldloads,
  getAllPendingApprovals,
} from '@store/Global/actions';
import {
  setWarningDataStore,
  sidebarDataStore,
  warningDataStore,
} from '@store/index';
import { OldLoadFilter, Sort } from '@store/loadboard';
import { CloseIcon, CachedIcon } from '@atgfr8/icons';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { size } from 'lodash';
import {
  createEffect,
  createSignal,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
} from 'solid-js';

import {
  ApprovalsWarningGrid,
  BillingHoldsWarningGrid,
  OldLoadWarningGrid,
} from './components';

const payload = {
  filters: [
    {
      field: 'days',
      filterType: 'EQUALS',
      values: ['45'],
    },
    {
      field: 'queue',
      filterType: 'EQUALS',
      values: ['true'],
    },
  ] as OldLoadFilter[],
  sort: {
    field: 'dropDate',
    direction: 'desc',
  } as Sort,
  pageNumber: 1,
  pageSize: 500,
};

const Warning = () => {
  const [tabs, setTabs] = createSignal<string[]>([]);
  const [activeTab, setActiveTab] = createSignal<number>(-1);
  const [loading, setLoading] = createSignal<boolean>(false);
  const currentTabName = () => {
    return activeTab() >= 0 && activeTab() < tabs().length
      ? tabs()[activeTab()]
      : '';
  };

  createEffect(() => {
    if (size(warningDataStore.billingHolds.data) > 0) {
      if (!tabs().includes('Billing Holds')) {
        setTabs([...tabs(), 'Billing Holds']);
      }
    }
    if (size(warningDataStore.oldLoad.data.data) > 0) {
      if (!tabs().includes('Old Loads')) {
        setTabs([...tabs(), 'Old Loads']);
      }
    }
    if (size(sidebarDataStore.approvals.data) > 0) {
      if (!tabs().includes('Approvals')) {
        setTabs([...tabs(), 'Approvals']);
      }
    }
  });

  const getCount = (header: string) => {
    switch (header) {
      case 'Billing Holds':
        return size(warningDataStore.billingHolds.data);
      case 'Approvals':
        return size(sidebarDataStore.approvals.data);
      case 'Old Loads':
        return size(warningDataStore.oldLoad.data.data);
    }
  };

  createEffect(() => {
    // Get reference to warning component
    const header = document.getElementById('warning-component');
    if (!header) return;

    // Create a ResizeObserver to watch for size changes
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        // When the component's size changes, update the height in the store
        const warningHeight = entry.contentRect.height;
        setWarningDataStore('warningComponent', 'height', warningHeight);
      }
    });

    // Start observing the warning component
    resizeObserver.observe(header);

    // Keep the window resize listener as a fallback
    window.addEventListener('resize', updateHeight);

    // Clean up when component unmounts
    onCleanup(() => {
      if (Boolean(header)) resizeObserver.unobserve(header);
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
    });
  });

  // Keep the original function for window resize events
  const updateHeight = () => {
    const header = document.getElementById('warning-component');
    const warningHeight = header ? header.offsetHeight : 0;
    setWarningDataStore('warningComponent', 'height', warningHeight);
  };

  const refresh = async () => {
    try {
      setLoading(true);
      // Clear only the tables related to warnings
      const db = getDB();
      if (db) {
        // Clear only these specific tables instead of all tables
        await Promise.all([
          db.billingHolds.clear(),
          db.oldLoads.clear(),
          db.pendingApprovals.clear(),
        ]);
      }
      await Promise.all([
        getAllBillingHolds(),
        getAllPendingApprovals(),
        getAlloldloads(payload),
      ]);
    } finally {
      setLoading(false);
    }
  };

  onMount(async () => {
    await Promise.all([
      getAllBillingHolds(),
      getAllPendingApprovals(),
      getAlloldloads(payload),
    ]);
  });
  return (
    <Show when={tabs().length > 0}>
      <Box class="bg-[#FF7373]! h-fit sticky shadow-lg p-2">
        <Box class="flex justify-between items-center flex-wrap">
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            class="flex space-x-2 justify-start pl-4 flex-wrap"
          >
            <Button
              variant="contained"
              color="primary"
              label="Check Again"
              startIcon={<CachedIcon size={20} />}
              onClick={refresh}
              isLoading={loading()}
              disabled={loading()}
              dataTestId={formatDataTestId('Check Again', 'button')}
            />
            <TabsComponent
              activeStyling="border-white"
              tabStyleClasses="hover:border-b-2 [&_p]:text-white"
              tabs={tabs().map((item) => {
                const count = getCount(item) ?? 0;
                return <Tab>{`${item} (${count})`}</Tab>;
              })}
              activeTab={activeTab()}
              onTabClick={setActiveTab}
            />
            <span class="text-base! font-semibold! text-[#ffffff]!">
              You are seeing this because these items REQUIRE your attention.
            </span>
          </Box>
        </Box>
        <Box
          class="flex items-center bg-red-800 h-6 w-7 rounded-sm justify-center absolute right-2 top-4"
          onClick={() => setActiveTab(-1)}
        >
          <CloseIcon class="cursor-pointer text-white text-sm!" />
        </Box>
      </Box>
      {activeTab() !== -1 && (
        <Box class="h-[390px] tablet:h-[320px] bg-[#E1ECF2] p-2">
          <Box class="bg-[#E9BABC] p-2 rounded-t-[8px] h-full">
            <Switch>
              <Match when={currentTabName() === 'Old Loads'}>
                <OldLoadWarningGrid setActiveTab={setActiveTab} />
              </Match>
              <Match when={currentTabName() === 'Billing Holds'}>
                <BillingHoldsWarningGrid setActiveTab={setActiveTab} />
              </Match>
              <Match when={currentTabName() === 'Approvals'}>
                <ApprovalsWarningGrid setActiveTab={setActiveTab} />
              </Match>
            </Switch>
          </Box>
        </Box>
      )}
    </Show>
  );
};

export default Warning;
